import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";
import Autocomplete from "@mui/material/Autocomplete";
import { DropdownChevron } from "component/shared/dropdown-chevron";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { getAgentsWithLeads } from "redux/selector";
import { Agent } from "model/agent";

const useStyles = makeStyles()((theme: Mui.Theme) => ({
	chipDeleteIcon: { color: theme.palette.text.primary, width: 16, height: 16 },
}));

// Force the Autocomplete dropdown to always open below the input (disable flip-to-top).
const BottomPopper = (props: any) => (
	<Mui.Popper {...props} placement="bottom-start" modifiers={{ flip: { enabled: false } }} />
);

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: (agents: Agent[]) => void;
}

const SELECT_ALL = "__select_all__";
type Option = Agent | typeof SELECT_ALL;

// Refresh "Add Agent for Round Robin Routing" modal (gated behind refreshDesignsDev).
// Mirrors the Geographic add-agent modal: a single multi-select agent picker with a
// top-checkbox "Select All". No postal codes — Round Robin only collects agents.
export const RefreshAddDialog: React.FC<Props> = ({ open, onClose, onSave }) => {
	const { classes } = useStyles();
	const agents = useSelector((state: RootState) => getAgentsWithLeads(state));

	const [selected, setSelected] = useState<Agent[]>([]);

	const allSelected = agents.length > 0 && agents.every((a) => selected.some((s) => s.id === a.id));
	const someSelected = selected.length > 0 && !allSelected;

	const reset = () => setSelected([]);
	const close = () => {
		reset();
		onClose();
	};
	const save = () => {
		if (selected.length === 0) return;
		onSave(selected);
		reset();
	};

	const options = useMemo<Option[]>(() => [SELECT_ALL, ...agents], [agents]);

	return (
		<Mui.Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
			<Mui.DialogTitle>
				<Mui.Box display="flex" alignItems="center" justifyContent="space-between">
					<Mui.Typography variant="h6" style={{ fontWeight: "bold", fontSize: 24 }}>Add Agent for Round Robin Routing</Mui.Typography>
					<Mui.IconButton size="small" onClick={close}>
						<Icons.X size={24} />
					</Mui.IconButton>
				</Mui.Box>
			</Mui.DialogTitle>
			<Mui.Divider style={{ marginLeft: 24, marginRight: 24 }} />
			<Mui.DialogContent style={{ padding: "16px 24px 16px" }}>
				<Mui.Typography variant="body1" style={{ marginBottom: 16 }}>
					Select one or more agents to add to the rotation. To create a weighted distribution, add an agent more than once.
				</Mui.Typography>

				<Mui.Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>Agents</Mui.Typography>
				<Autocomplete
					multiple
					disableCloseOnSelect
					options={options}
					value={selected}
					getOptionLabel={(o) => (o === SELECT_ALL ? "Select All" : (o as Agent).name || "")}
					isOptionEqualToValue={(o, v) =>
						o === SELECT_ALL || v === SELECT_ALL ? o === v : (o as Agent).id === (v as Agent).id
					}
					popupIcon={<DropdownChevron />}
					PopperComponent={BottomPopper}
					onChange={(event, values) => {
						if ((values as Option[]).includes(SELECT_ALL)) {
							setSelected(allSelected ? [] : agents);
						} else {
							setSelected(values as Agent[]);
						}
					}}
					renderOption={(props, option, { selected: isSelected }) => (
						<li {...props}>
							<Mui.Checkbox
								color="secondary"
								style={{ marginRight: 8 }}
								checked={option === SELECT_ALL ? allSelected : isSelected}
								indeterminate={option === SELECT_ALL ? someSelected : undefined}
							/>
							{option === SELECT_ALL ? <strong>Select All</strong> : (option as Agent).name}
						</li>
					)}
					renderTags={(value, getTagProps) =>
						value.map((option, index) => {
							const { key, ...tagProps } = getTagProps({ index });
							return (
								<Mui.Chip
									key={key}
									label={(option as Agent).name}
									deleteIcon={<Icons.X size={12} />}
									classes={{ deleteIcon: classes.chipDeleteIcon }}
									{...tagProps}
								/>
							);
						})
					}
					renderInput={(params) => (
						<Mui.TextField {...params} variant="outlined" placeholder={selected.length === 0 ? "Select one or more agents" : undefined} />
					)}
				/>
			</Mui.DialogContent>
			<Mui.DialogActions style={{ padding: "8px 24px 24px" }}>
				<Mui.Button variant="outlined" color="primary" onClick={close}>
					Cancel
				</Mui.Button>
				<Mui.Button variant="contained" color="primary" disabled={selected.length === 0} onClick={save}>
					Add to Rotation
				</Mui.Button>
			</Mui.DialogActions>
		</Mui.Dialog>
	);
};
